import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Spinner,
  Alert,
} from '@design-system';
import { Sparkles, AlertCircle } from 'lucide-react';
import { FundingPolicyType, getAiPromptTypeByPolicyType, isOutdatedVersion } from '@shared';
import { useTriggerUnderwriting } from '../hooks/use-trigger-underwriting';
import { useRequestPolling } from '../hooks/use-request-polling';
import { UnderwritingSlideOut } from './underwriting-slide-out';

interface PreUnderwritingFormCTAProps {
  formData: PolicyFormSubmissionData;
  fundingPolicyType: FundingPolicyType;
  buttonText?: string;
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disableAbsolutePosition?: boolean;
}

/**
 * Pre-underwriting form CTA component
 *
 * Refactored from AiUnderwritingGuidance. Core logic is preserved and can be refactored further.
 */
export function PreUnderwritingFormCTA({
  formData,
  fundingPolicyType,
  buttonVariant = 'secondary',
  buttonSize = 'default',
  className = '',
  disableAbsolutePosition = false,
}: PreUnderwritingFormCTAProps) {
  const [isOpen, setIsOpen] = useState(false);
  const previousFormDataRef = useRef<PolicyFormSubmissionData | undefined>(undefined);
  const formDataChangedRef = useRef(false);
  const [requestId, setRequestId] = useState<string>('');
  const [hasTriggeredUnderwriting, setHasTriggeredUnderwriting] = useState(false);

  // Trigger underwriting and get requestId
  const {
    mutate: triggerUnderwriting,
    data: triggerData,
    isPending,
    statusCode,
  } = useTriggerUnderwriting();

  // Store requestId when trigger completes
  useEffect(() => {
    if (triggerData) {
      setRequestId(triggerData);
    }
  }, [triggerData]);

  // Poll for underwriting results
  const {
    data: pollingData,
    isPolling,
    fetchingError: pollingError,
  } = useRequestPolling(requestId);

  const formDataTooShort = useMemo(
    () => !formData || Object.keys(formData).length <= 5,
    [formData],
  );

  // Detect formData changes
  useEffect(() => {
    if (!formData) return;
    const previousFormData = previousFormDataRef.current;
    const currentFormData = formData;

    // On first render, store the initial formData
    if (previousFormData === undefined) {
      previousFormDataRef.current = currentFormData;
      formDataChangedRef.current = false;
      return;
    }

    // Compare form data to detect meaningful changes (ignoring timestamps, etc.)
    const formDataChanged = JSON.stringify(previousFormData) !== JSON.stringify(currentFormData);

    if (formDataChanged) {
      formDataChangedRef.current = true;
      previousFormDataRef.current = currentFormData;
    }
  }, [formData]);

  // Track when underwriting is triggered
  useEffect(() => {
    if (isPending) {
      setHasTriggeredUnderwriting(true);
    }
  }, [isPending]);

  // Reset hasTriggeredUnderwriting when polling starts
  useEffect(() => {
    if (isPolling) {
      setHasTriggeredUnderwriting(false);
    }
  }, [isPolling]);

  // Reset hasTriggeredUnderwriting when polling completes
  useEffect(() => {
    if (pollingData?.status === 'completed') {
      setHasTriggeredUnderwriting(false);
    }
  }, [pollingData?.status]);

  // Detect if the error is a rate limit error
  const isRateLimitError =
    (typeof statusCode === 'string' && statusCode === '429') ||
    (typeof statusCode === 'number' && statusCode === 429);

  // Keep loading state continuous: loading if pending, polling, OR in the gap between triggering and polling
  const isLoading = isPending || isPolling || hasTriggeredUnderwriting;

  const handleGetUnderwritingGuidance = () => {
    const triggerUnderwritingOptions = {
      fundingPolicyType,
      formData,
    };
    triggerUnderwriting(triggerUnderwritingOptions);
  };

  const isButtonDisabled = formDataTooShort || !formDataChangedRef.current || isRateLimitError;

  const getTooltipMessage = () => {
    if (formDataTooShort) {
      return 'Please fill out more information in the form before requesting underwriting guidance.';
    }
    if (!formDataChangedRef.current) {
      return 'No changes detected in the form. Please update the form data to get new underwriting guidance.';
    }
    return '';
  };

  // Open slide-out only when polling data is completed
  useEffect(() => {
    if (pollingData?.status === 'completed') {
      setIsOpen(true);
    }
  }, [pollingData?.status]);

  const button = (
    <Button
      onClick={() => {
        handleGetUnderwritingGuidance();
      }}
      variant={buttonVariant}
      size={buttonSize}
      className={`flex items-center gap-2 ${className}`}
      data-testid="get-underwriting-guidance"
      disabled={isButtonDisabled || isLoading}
    >
      {isLoading ? (
        <>
          <Spinner className="h-4 w-4" />
          Generating Guidance...
        </>
      ) : (
        <>
          <Sparkles className="h-4 w-4" />
          PUI Based Underwriting Guidance
        </>
      )}
    </Button>
  );

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex ${disableAbsolutePosition ? '' : 'lg:absolute lg:right-0'} justify-start md:justify-center`}
      >
        {isButtonDisabled && !isRateLimitError ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{button}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getTooltipMessage()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          button
        )}
      </div>

      {isRateLimitError && (
        <Alert
          variant="destructive"
          className="mt-2"
        >
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-semibold">Rate limit reached</p>
            <p className="text-sm">
              You've reached the maximum number of underwriting requests. This helps us ensure
              quality responses. Please wait a few minutes before trying again.
            </p>
          </div>
        </Alert>
      )}

      <UnderwritingSlideOut
        open={isOpen}
        onClose={() => setIsOpen(false)}
        underwritingResult={
          pollingData?.status === 'completed' ? pollingData.results || null : null
        }
        productName="PUI Based Underwriting"
      />
    </div>
  );
}
